import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { trackWhatsAppLead } from '../hooks/useAnalytics';
import { ShoppingBag, X, Trash2, ShieldCheck, Plus, Minus, Info, Menu, ArrowLeft, MapPin, User, Send, CreditCard } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectItems, selectIsCartOpen, selectTotalItems, selectTotalPrice,
  selectTotalSavings, getItemSubtotal,
  removeFromCart, updateQuantity, clearCart, setIsCartOpen,
} from '../store/cartSlice';
import { useGetProfileQuery } from '../store/customersApi';
import { useGetShippingQuoteMutation, useCreateOrderMutation } from '../store/ordersApi';
import { useGetPublicSettingsQuery } from '../store/settingsApi';
import { DrawerPanel, PriceDisplay, AlertModal } from './ui';

interface NavbarProps {
  onOpenCategories?: () => void;
  activeView?: 'inicio' | 'catalogo';
  onViewChange?: (view: 'inicio' | 'catalogo') => void;
}

const phone = import.meta.env.VITE_WHATSAPP_PHONE as string;

export const Navbar: React.FC<NavbarProps> = ({
  onOpenCategories,
  activeView,
  onViewChange,
}) => {
  const { pathname } = useLocation();
  const currentView = activeView || (pathname === '/catalogo' ? 'catalogo' : 'inicio');
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    message: string;
    title?: string;
    variant?: 'error' | 'warning' | 'success' | 'info';
    onConfirm?: () => void;
    confirmText?: string;
    cancelText?: string;
  } | null>(null);

  // Checkout form states
  const [customerName, setCustomerName] = useState('');
  const [deliveryType, setDeliveryType] = useState<'envio' | 'retiro'>('retiro');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [notes, setNotes] = useState('');

  const navigate = useNavigate();
  const [mpAddress, setMpAddress] = useState('');
  const [mpLocality, setMpLocality] = useState('');
  const [mpProvince, setMpProvince] = useState('');
  const [mpPostalCode, setMpPostalCode] = useState('');
  const [shippingCost, setShippingCost] = useState<number | null>(null);
  const [getShippingQuote] = useGetShippingQuoteMutation();
  const [createOrder, { isLoading: isCreatingOrder }] = useCreateOrderMutation();
  // Mientras carga o si falla la consulta, se asume apagado (fallo seguro).
  const { data: publicSettings } = useGetPublicSettingsQuery();
  const mercadopagoEnabled = publicSettings?.mercadopagoEnabled === true;

  const dispatch = useAppDispatch();
  const items = useAppSelector(selectItems);
  const isCartOpen = useAppSelector(selectIsCartOpen);
  const totalItems = useAppSelector(selectTotalItems);
  const totalPrice = useAppSelector(selectTotalPrice);
  const totalSavings = useAppSelector(selectTotalSavings);
  const customer = useAppSelector((s) => s.customerAuth.customer);
  const customerToken = useAppSelector((s) => s.customerAuth.token);
  useGetProfileQuery(undefined, { skip: !customerToken || !!customer });

  useEffect(() => {
    if (customer) {
      setMpAddress(customer.address);
      setMpLocality(customer.addressLocality);
      setMpProvince(customer.addressProvince);
      setMpPostalCode(customer.addressPostalCode);
    }
  }, [customer]);

  useEffect(() => {
    if (!showCheckoutForm || !customer || items.length === 0) {
      setShippingCost(null);
      return;
    }
    let cancelled = false;
    getShippingQuote({
      items: items.map(i => ({ productId: i.product.id, quantity: i.quantity })),
      deliveryType,
    }).unwrap()
      .then(({ shippingCost }) => { if (!cancelled) setShippingCost(shippingCost); })
      .catch(() => { if (!cancelled) setShippingCost(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCheckoutForm, customer, deliveryType, items.length]);

  const handleSendWhatsApp = () => {
    if (!customerName.trim()) {
      setAlertModal({ title: 'Nombre requerido', message: 'Por favor, ingresá tu nombre completo.' });
      return;
    }
    if (deliveryType === 'envio' && (!address.trim() || !city.trim())) {
      setAlertModal({ title: 'Datos de envío incompletos', message: 'Por favor, completá la dirección y localidad de entrega.' });
      return;
    }

    // Formatear el mensaje de WhatsApp con emojis
    let message = `🛍️ *NUEVO PEDIDO - AYE OTERO CALZADOS*\n\n`;
    message += `👤 *Cliente:* ${customerName}\n`;
    message += `🚚 *Método:* ${deliveryType === 'envio' ? 'Envío a domicilio' : 'Retiro por local (Belgrano 422)'}\n`;
    if (deliveryType === 'envio') {
      message += `📍 *Dirección:* ${address}\n`;
      message += `🏙️ *Localidad:* ${city}\n`;
    }
    if (notes.trim()) {
      message += `📝 *Notas:* ${notes}\n`;
    }
    message += `\n----------------------------------\n`;
    message += `📦 *Detalle del Pedido:*\n\n`;

    items.forEach((item) => {
      const subtotal = getItemSubtotal(item);
      const hasPromo = !!item.product.promotion;
      message += `• *${item.product.name}*\n`;
      message += `  Talle: ${item.size} | Cant: ${item.quantity} x $${Math.round(item.product.price)}`;
      if (hasPromo) {
        message += ` (Promo: ${item.product.promotion!.label})`;
      }
      message += `\n  Subtotal: *$${Math.round(subtotal)}*`;
      if (hasPromo) {
        const originalSubtotal = item.product.price * item.quantity;
        const saved = originalSubtotal - subtotal;
        if (saved > 0) {
          message += ` (Ahorro: -$${Math.round(saved)})`;
        }
      }
      message += `\n\n`;
    });

    message += `----------------------------------\n`;
    if (totalSavings > 0) {
      const originalTotal = totalPrice + totalSavings;
      message += `💰 *Subtotal:* $${Math.round(originalTotal)}\n`;
      message += `🏷️ *Ahorro por Promos:* -$${Math.round(totalSavings)}\n`;
    }
    message += `💰 *TOTAL GENERAL: $${Math.round(totalPrice)}*\n\n`;
    message += `✨ _Gracias por elegir Aye Otero Calzados. Envíos a todo el país._`;

    // Redirigir a WhatsApp
    const encodedMessage = encodeURIComponent(message);
    const url = `https://wa.me/${phone}?text=${encodedMessage}`;

    window.open(url, '_blank');

    trackWhatsAppLead({
      customerName: customerName.trim(),
      items: items.map(i => ({
        productId: i.product.id,
        name: i.product.name,
        size: i.size,
        quantity: i.quantity,
        price: i.product.price,
      })),
      totalAmount: totalPrice,
      deliveryType: deliveryType,
    });

    // Preguntar al usuario si desea vaciar el carrito una vez redirigido
    setAlertModal({
      title: '¡Redirigiendo a WhatsApp!',
      message: '¿Pudiste enviar tu pedido con éxito y deseas vaciar tu carrito de compras?',
      variant: 'success',
      confirmText: 'Sí, vaciar carrito',
      cancelText: 'No, conservar productos',
      onConfirm: () => {
        dispatch(clearCart());
        setCustomerName('');
        setAddress('');
        setCity('');
        setNotes('');
        setShowCheckoutForm(false);
        dispatch(setIsCartOpen(false));
        setAlertModal(null);
      }
    });
  };

  const handlePayWithMercadoPago = async () => {
    if (!customer) {
      dispatch(setIsCartOpen(false));
      navigate('/login');
      return;
    }
    if (deliveryType === 'envio' && (!mpAddress.trim() || !mpLocality.trim() || !mpProvince.trim() || !mpPostalCode.trim())) {
      setAlertModal({ title: 'Dirección incompleta', message: 'Completá la dirección de envío para continuar.' });
      return;
    }

    try {
      const { initPoint } = await createOrder({
        items: items.map(i => ({ productId: i.product.id, size: i.size, quantity: i.quantity })),
        deliveryType,
        ...(deliveryType === 'envio' ? {
          shippingAddress: mpAddress.trim(),
          shippingLocality: mpLocality.trim(),
          shippingProvince: mpProvince.trim(),
          shippingPostalCode: mpPostalCode.trim(),
        } : {}),
      }).unwrap();
      window.location.href = initPoint;
    } catch (e: any) {
      setAlertModal({ title: 'No se pudo iniciar el pago', message: e?.data?.message ?? 'Intentá de nuevo en unos minutos.' });
    }
  };

  return (
    <>
      <AlertModal
        isOpen={!!alertModal}
        onClose={() => setAlertModal(null)}
        title={alertModal?.title}
        message={alertModal?.message ?? ''}
        variant={alertModal?.variant ?? 'warning'}
        onConfirm={alertModal?.onConfirm}
        confirmText={alertModal?.confirmText}
        cancelText={alertModal?.cancelText}
      />

      <header className="navbar-fixed bg-white/80 backdrop-blur-xl border-b border-slate-200/60 px-6 lg:px-16 py-4 flex items-center justify-between shadow-sm">
        {/* Logo and Menu Trigger */}
        <div className="flex items-center gap-3">
          {onOpenCategories && (
            <button
              onClick={onOpenCategories}
              className="lg:hidden bg-white border border-slate-200 p-2.5 rounded-xl hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-all active:scale-95 flex items-center justify-center shadow-sm"
              aria-label="Abrir menú de categorías"
            >
              <Menu className="w-5 h-5 text-indigo-600" />
            </button>
          )}

          <Link
            to="/"
            className="flex items-center gap-3 cursor-pointer select-none active:scale-[0.98] transition-transform"
            title="Ir al Inicio"
          >
            <img src="/icon.png" alt="Aye Calzados" className="w-12 h-12 rounded-full object-cover" />
            <div>
              <h1 className="font-heading font-bold text-lg text-slate-900 leading-none tracking-wide">Aye Otero</h1>
              <span className="text-[10px] text-indigo-600 font-semibold tracking-widest uppercase mt-0.5 block">Calzados y Moda</span>
            </div>
          </Link>
        </div>

        {/* Navigation Items */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <Link
            to="/"
            className={`transition-colors font-semibold ${currentView === 'inicio' ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'}`}
          >
            Inicio
          </Link>
          <Link
            to="/catalogo"
            className={`transition-colors font-semibold ${currentView === 'catalogo' ? 'text-indigo-600' : 'text-slate-600 hover:text-indigo-600'}`}
          >
            Catálogo
          </Link>
          <a
            href={`https://wa.me/${phone}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-600 hover:text-indigo-600 transition-colors font-semibold"
          >
            Contacto
          </a>
        </nav>

        {/* Cart Trigger */}
        <div className="flex items-center gap-4">
          <Link
            to={customer ? '/mi-cuenta' : '/login'}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-indigo-600 transition-colors"
          >
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">{customer ? customer.name.split(' ')[0] : 'Iniciar sesión'}</span>
          </Link>
          {pathname !== '/' && (
            <button
              onClick={() => { dispatch(setIsCartOpen(true)); setShowCheckoutForm(false); }}
              className="relative bg-indigo-500/10 hover:bg-indigo-500 hover:text-[#333333] text-indigo-700 transition-all duration-200 group p-3 rounded-xl border border-indigo-200/50 shadow-sm animate-fade-in"
            >
              <ShoppingBag className="w-5 h-5 group-hover:scale-105 transition-transform" />
              {totalItems > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white font-extrabold text-[9px] min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center border-2 border-white shadow-lg shadow-blue-500/20">
                  {totalItems}
                </span>
              )}
            </button>
          )}
        </div>
      </header>

      {/* Shopping Cart Drawer */}
      <DrawerPanel isOpen={isCartOpen} onClose={() => dispatch(setIsCartOpen(false))} side="right" width="max-w-md">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-indigo-600" />
            <h3 className="font-heading font-bold text-lg text-slate-900">
              {showCheckoutForm ? 'Datos del Pedido' : 'Carrito de Compras'}
            </h3>
          </div>
          <button
            onClick={() => dispatch(setIsCartOpen(false))}
            className="text-slate-500 hover:text-slate-900 p-1.5 hover:bg-slate-200/60 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto space-y-4">
          {showCheckoutForm ? (
            <div className="space-y-4 animate-fade-in">
              <button
                onClick={() => setShowCheckoutForm(false)}
                className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 font-bold uppercase tracking-wider transition-colors mb-2 animate-pulse"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Volver al Carrito
              </button>

              <div className="space-y-3.5 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <h4 className="text-xs text-slate-550 uppercase tracking-widest font-bold font-heading">Tus Datos de Contacto</h4>

                {/* Nombre y Apellido */}
                <div className="space-y-1">
                  <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Nombre Completo *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Ej. Juan Pérez"
                      className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-colors"
                      required
                    />
                    <User className="w-3.5 h-3.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                {/* Tipo de Entrega */}
                <div className="space-y-1">
                  <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Método de Entrega</label>
                  <div className="flex gap-2 bg-slate-100 p-1 rounded-xl border border-slate-200/60">
                    <button
                      type="button"
                      onClick={() => setDeliveryType('envio')}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${deliveryType === 'envio'
                        ? 'bg-indigo-500 text-[#333333] shadow-sm'
                        : 'text-slate-500 hover:text-slate-800'
                        }`}
                    >
                      Envío a domicilio
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeliveryType('retiro')}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${deliveryType === 'retiro'
                        ? 'bg-indigo-500 text-[#333333] shadow-sm'
                        : 'text-slate-500 hover:text-slate-800'
                        }`}
                    >
                      Retirar por local
                    </button>
                  </div>
                </div>

                {/* Datos de envío (si aplica) */}
                {deliveryType === 'envio' && (
                  <div className="space-y-3.5 pt-1.5 border-t border-slate-200/60 animate-fade-in">
                    <div className="space-y-1">
                      <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Dirección de Entrega *</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          placeholder="Calle, Altura, Piso, Dpto"
                          className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-colors"
                          required={deliveryType === 'envio'}
                        />
                        <MapPin className="w-3.5 h-3.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Localidad / Provincia *</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="Ej. Capitán Sarmiento, Buenos Aires"
                          className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-colors"
                          required={deliveryType === 'envio'}
                        />
                        <MapPin className="w-3.5 h-3.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      </div>
                    </div>

                    {mercadopagoEnabled && customer && (
                      <div className="space-y-3.5 pt-3.5 border-t border-slate-200/60 animate-fade-in">
                        <h4 className="text-xs text-slate-550 uppercase tracking-widest font-bold font-heading">
                          Dirección para pago online (Mercado Pago)
                        </h4>
                        <input
                          type="text"
                          value={mpAddress}
                          onChange={(e) => setMpAddress(e.target.value)}
                          placeholder="Calle y altura"
                          className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-colors"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={mpLocality}
                            onChange={(e) => setMpLocality(e.target.value)}
                            placeholder="Localidad"
                            className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-colors"
                          />
                          <input
                            type="text"
                            value={mpProvince}
                            onChange={(e) => setMpProvince(e.target.value)}
                            placeholder="Provincia"
                            className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-colors"
                          />
                        </div>
                        <input
                          type="text"
                          value={mpPostalCode}
                          onChange={(e) => setMpPostalCode(e.target.value)}
                          placeholder="Código postal"
                          className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-colors"
                        />
                      </div>
                    )}
                  </div>
                )}

                {deliveryType === 'retiro' && (
                  <div className="p-3 bg-indigo-500/5 border border-indigo-500/20 rounded-xl space-y-1 text-slate-700 text-xs animate-fade-in leading-normal flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0 animate-bounce" />
                    <div>
                      <p className="font-bold text-indigo-600">Punto de Retiro:</p>
                      <p>Belgrano 422, Capitán Sarmiento, Buenos Aires.</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Coordinaremos el horario de retiro una vez enviado el pedido.</p>
                    </div>
                  </div>
                )}

                {/* Notas */}
                <div className="space-y-1">
                  <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Aclaraciones / Notas (Opcional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Ej. Talles específicos, color de preferencia, aclaraciones..."
                    rows={3}
                    className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl px-4 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-colors resize-none"
                  />
                </div>
              </div>
            </div>
          ) : (
            // Cart Items List
            items.length === 0 ? (
              <div className="text-center py-20 text-slate-500 flex flex-col items-center justify-center space-y-3">
                <ShoppingBag className="w-10 h-10 text-slate-400 stroke-[1.5]" />
                <p className="text-sm font-medium text-slate-700">Tu carrito de compras está vacío.</p>
                <p className="text-xs text-slate-450 max-w-[200px]">Elige talles y agrega productos desde el catálogo.</p>
              </div>
            ) : (
              items.map((item, idx) => (
                <div key={`${item.product.id}-${item.size}-${idx}`} className="flex gap-4 p-3.5 bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-colors shadow-sm animate-fade-in">
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    loading="lazy"
                    decoding="async"
                    className="w-16 h-16 rounded-lg object-cover border border-slate-100 bg-slate-50 flex-shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=100&auto=format&fit=crop&q=60';
                    }}
                  />
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <h4 className="font-semibold text-slate-800 text-sm truncate">{item.product.name}</h4>
                        <span className="inline-block mt-1 text-[9px] bg-slate-100 border border-slate-200 text-slate-600 font-bold px-1.5 py-0.5 rounded uppercase">
                          Talle: {item.size}
                        </span>
                      </div>
                      <button
                        onClick={() => dispatch(removeFromCart({ productId: item.product.id, size: item.size }))}
                        className="text-slate-400 hover:text-rose-500 p-1 rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="flex justify-between items-center mt-3">
                      <div className="flex flex-col">
                        <PriceDisplay price={getItemSubtotal(item)} size="sm" />
                        {item.product.promotion && item.quantity > 0 && (
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[9px] text-slate-400 line-through">
                              ${(item.product.price * item.quantity).toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                            </span>
                            <span className="text-[9px] text-emerald-600 font-extrabold uppercase">
                              {item.product.promotion.label}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-hidden h-7">
                        <button
                          onClick={() => dispatch(updateQuantity({ productId: item.product.id, size: item.size, quantity: item.quantity - 1 }))}
                          className="px-2 text-slate-500 hover:text-slate-800 hover:bg-slate-200/80 h-full flex items-center justify-center transition-colors"
                        >
                          <Minus className="w-2.5 h-2.5" />
                        </button>
                        <span className="px-2.5 text-xs text-slate-700 font-bold bg-white h-full flex items-center border-x border-slate-200/60">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => dispatch(updateQuantity({ productId: item.product.id, size: item.size, quantity: item.quantity + 1 }))}
                          className="px-2 text-slate-500 hover:text-slate-800 hover:bg-slate-200/80 h-full flex items-center justify-center transition-colors"
                        >
                          <Plus className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="p-6 border-t border-slate-200 bg-slate-50 space-y-4">
            <div className="space-y-1.5 border-b border-slate-200/60 pb-3">
              {totalSavings > 0 && (
                <>
                  <div className="flex justify-between items-center text-xs text-slate-500">
                    <span>Subtotal</span>
                    <span>${(totalPrice + totalSavings).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-emerald-600 font-semibold">
                    <span>Descuento Promos</span>
                    <span>-${totalSavings.toLocaleString('es-AR', { maximumFractionDigits: 0 })}</span>
                  </div>
                </>
              )}
              {customer && showCheckoutForm && shippingCost !== null && (
                <div className="flex justify-between items-center text-xs text-slate-600">
                  <span>Costo de envío (pago online)</span>
                  <span>${shippingCost.toLocaleString('es-AR', { maximumFractionDigits: 0 })}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-1.5">
                <span className="text-slate-700 text-sm font-semibold">Total Pedido</span>
                <PriceDisplay price={totalPrice + (shippingCost ?? 0)} size="lg" />
              </div>
            </div>

            <p className="text-[10px] text-slate-600 text-center leading-normal flex items-center gap-1.5 justify-center bg-[#f3f0e9]/80 p-2.5 rounded-xl border border-slate-200/60">
              <Info className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
              Realizamos envíos a todo el país desde Capitán Sarmiento.
            </p>

            <div className="grid grid-cols-2 gap-3 mt-2">
              {showCheckoutForm ? (
                <div className="col-span-2 space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setShowCheckoutForm(false)}
                      className="py-3 px-4 border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-semibold transition-colors"
                    >
                      Atrás
                    </button>
                    {mercadopagoEnabled ? (
                      <button
                        onClick={handlePayWithMercadoPago}
                        disabled={isCreatingOrder}
                        className="py-3 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-blue-500/10 transition-all duration-200 flex items-center justify-center gap-1.5 disabled:opacity-50"
                      >
                        <CreditCard className="w-3 h-3" />
                        {customer ? 'Pagar con Mercado Pago' : 'Iniciar sesión para pagar'}
                      </button>
                    ) : (
                      <button
                        onClick={handleSendWhatsApp}
                        className="py-3 px-4 bg-[#25d366] hover:bg-[#20ba56] text-white rounded-xl text-xs font-bold shadow-lg shadow-emerald-500/10 transition-all duration-200 flex items-center justify-center gap-1.5"
                      >
                        <Send className="w-3 h-3" /> Enviar pedido por WhatsApp
                      </button>
                    )}
                  </div>
                  {mercadopagoEnabled && (
                    <button
                      onClick={handleSendWhatsApp}
                      className="w-full py-3 px-4 bg-[#25d366] hover:bg-[#20ba56] text-white rounded-xl text-xs font-bold shadow-lg shadow-emerald-500/10 transition-all duration-200 flex items-center justify-center gap-1.5"
                    >
                      <Send className="w-3 h-3" /> Enviar pedido por WhatsApp
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <button
                    onClick={() => dispatch(clearCart())}
                    className="py-3 px-4 border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-semibold transition-colors"
                  >
                    Vaciar
                  </button>
                  <button
                    onClick={() => setShowCheckoutForm(true)}
                    className="py-3 px-4 bg-indigo-500 hover:bg-indigo-600 text-[#333333] hover:text-white border border-indigo-500/20 rounded-xl text-xs font-bold shadow-lg shadow-indigo-500/10 transition-all duration-200"
                  >
                    Confirmar Pedido
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </DrawerPanel>
    </>
  );
};

export default Navbar;
